import { AxiosError } from "axios";

/**
 * The reason the API gave for rejecting a request, or `fallback` when it did not
 * give a usable one.
 *
 * Every API/V1 controller renders its failures through
 * `DiverstController#error_json`, which answers `{ message, attribute, errors }`
 * where `message` is a human readable sentence naming what was rejected. That is
 * a far better thing to put in front of a user than "Failed to update X", so
 * mutation hooks prefer it over their own generic copy.
 *
 * This is a *fallback* path - with correct client side validation a mutation
 * should not fail in the first place - so it is deliberately defensive. The body
 * is whatever the server happened to send: a rejected promise that never reached
 * the network has no `response` at all, a `502` from a proxy answers HTML, an
 * empty body deserialises to `""`, and a hand rolled action could put something
 * other than a string under `message`. Rendering any of those would produce
 * `undefined` or `[object Object]` in a snackbar, which is worse than the
 * generic copy, so anything that is not a non-blank string falls back.
 *
 * The server message is not translatable - it is composed by the backend in the
 * requesting user's locale, if at all - so it is passed through verbatim rather
 * than being fed to `react-intl`. Only the `fallback` is localised, by the
 * caller.
 *
 * ⚠️ API/V2 (Spring Boot) does **not** use this shape. Do not reach for this
 * helper from a hook that calls `/api/v2/**` or a generated client without first
 * establishing what that endpoint actually returns.
 *
 * @param error The rejection react query handed to `onError`
 * @param fallback The hook's own generic, translated copy
 * @returns The server's message, or `fallback`
 */
export function apiErrorMessage(
  error: AxiosError<{ message?: string }>,
  fallback: string,
): string {
  const body: unknown = error.response?.data;
  if (typeof body !== "object" || body === null) {
    return fallback;
  }

  const { message } = body as { message?: unknown };
  return typeof message === "string" && message.trim().length > 0
    ? message
    : fallback;
}
